import { BasketProduct } from '../../models/basket/basket-product';
import { PurchaseListItem } from '../../models/purchase-list-item';
import { ProductListItem } from '../../models/product-list/productListItem';

/**
 * Traduit le contenu de la liste d'achat vers les produits d'une configuration panier, et
 * inversement. Les bundles sont représentés par un parent porteur de ses enfants côté configuration,
 * et à plat côté liste d'achat (parent marqué {@link PurchaseListItem.isBundleParent}, enfants
 * rattachés par {@link PurchaseListItem.bundleParentEan}).
 */
export class BasketProductMapper {
  /** Convertit les lignes de la liste d'achat en produits de configuration, bundles regroupés. */
  static toBasketProducts(purchaseList: PurchaseListItem[]): BasketProduct[] {
    const records: BasketProduct[] = [];

    purchaseList
      .filter(item => !item.isBundleParent && !item.bundleParentEan)
      .forEach(item => records.push(BasketProductMapper.toBasketProduct(item)));

    purchaseList
      .filter(item => item.isBundleParent)
      .forEach(parent => {
        const children = purchaseList.filter(item => item.bundleParentEan === parent.product.ean);
        records.push(BasketProductMapper.toBundleBasketProduct(parent, children));
      });

    return records;
  }

  /** Convertit les produits d'une configuration en lignes de liste d'achat, bundles mis à plat. */
  static toPurchaseListItems(basketProducts: BasketProduct[]): PurchaseListItem[] {
    const items: PurchaseListItem[] = [];

    basketProducts.forEach(basketProduct => {
      if (!basketProduct.bundleProducts?.length) {
        items.push({ product: BasketProductMapper.toProductListItem(basketProduct), amount: basketProduct.quantity });
        return;
      }

      items.push({
        product: BasketProductMapper.toProductListItem(basketProduct),
        amount: basketProduct.quantity,
        isBundleParent: true,
      });

      basketProduct.bundleProducts.forEach(child =>
        items.push({
          product: BasketProductMapper.toProductListItem(child),
          amount: child.quantity,
          bundleParentEan: basketProduct.ean,
        })
      );
    });

    return items;
  }

  private static toBasketProduct(purchaseItem: PurchaseListItem): BasketProduct {
    return Object.assign(new BasketProduct(), {
      nameFr: purchaseItem.product.name,
      nameEn: purchaseItem.product.name,
      price: purchaseItem.product.price,
      pv: purchaseItem.product.price,
      ean: purchaseItem.product.ean,
      iconId: -1,
      ecotaxId: 0,
      ecotax: null,
      quantity: purchaseItem.amount,
      imgPath: purchaseItem.product.imgPaths.length > 0 ? purchaseItem.product.imgPaths[0] : '',
      additionnalQuantity: 0,
      additionnalQuantityUnit: '',
      categoryNameFr: 'produit',
      categoryNameEn: 'product',
      additionalInfo: '',
      additionalInfoQuotation: '',
    });
  }

  private static toBundleBasketProduct(parent: PurchaseListItem, children: PurchaseListItem[]): BasketProduct {
    const bundle = BasketProductMapper.toBasketProduct(parent);
    bundle.bundleProducts = children.map(child => BasketProductMapper.toBasketProduct(child));
    return bundle;
  }

  private static toProductListItem(basketProduct: BasketProduct): ProductListItem {
    return ProductListItem.fromPartial({
      ean: basketProduct.ean,
      name: basketProduct.nameFr,
      // Une configuration rechargée ne porte que `pv` : `price` n'existe qu'à l'aller.
      price: basketProduct.pv ?? basketProduct.price,
      imgPaths: basketProduct.imgPath ? [basketProduct.imgPath] : [],
    });
  }
}
